/**
 * The one rule for "which rooms/cities may the public see".
 *
 * Imported by both `src/pages/Properties.tsx` (the live grid) and
 * `scripts/prerender.ts` (the build-time SEO pass) so the sitemap, the
 * prerendered `/rooms/:city` pages and the runtime grid can never drift. If
 * they disagree, junk URLs leak into the index or a real city 404s.
 *
 * Pure data + pure predicates only: no React, no Vite env, no Supabase — that
 * is what lets the prerender script (plain `tsx`) import it.
 */

/** The room columns the public listing rules read (the query selects more). */
export type PublicRoomRow = {
  is_hidden?: boolean | null;
  title?: string | null;
  properties?: { is_hidden?: boolean | null; city?: string | null } | null;
};

/** CMS image labels that ride in the rooms table but aren't bookable rooms. */
const NON_ROOM_TITLES = new Set(["villa", "kitchen", "staircase"]);

/**
 * A room the public may see at all: its property isn't hidden, the room isn't
 * hidden, and it's a real room rather than a property-image label. Availability
 * is a separate concern — an occupied room still lists (as "Unavailable").
 */
export function isPublicRoom(room: PublicRoomRow): boolean {
  if (room.properties?.is_hidden === true || room.is_hidden === true)
    return false;
  return !NON_ROOM_TITLES.has((room.title || "").trim().toLowerCase());
}

/**
 * Distinct cities of the published, non-hidden listings — the only cities a
 * `/rooms/<city>` URL may resolve to, and the only ones the sitemap lists.
 * Built from the visibility filter, not availability: a city whose rooms are
 * all temporarily occupied still has a valid (correctly empty) page.
 */
export function publishedCities(rooms: PublicRoomRow[]): string[] {
  return [
    ...new Set(
      rooms
        .filter(isPublicRoom)
        .map((r) => r.properties?.city)
        .filter((c): c is string => Boolean(c))
    ),
  ];
}
